import axios from "axios";
import { Observable } from "rxjs";
import { NotImplementedError } from "../../../errors";
import { ObservableHandler } from "../../../handlers";
import { parseIdFilter } from "../../../handlers/filter/utils";
import { AdvertiserProfileFetchFailed, AdvertiserProfileResearchFailed, AdvertiserProfileSaveFailed } from "./errors";
import { isAdvertiserProfileUpdatablePath } from "./types";
import { DEFAULT_PAGE_SIZE, toQueryParams } from "./utils";
/**
 * Field mask for a partial update. Read-only fields are dropped — the service
 * rejects the whole request if the mask names one, and callers routinely hand
 * over a whole loaded profile. `contact` is expanded to the nested paths the
 * service accepts, since the parent on its own is not one of them.
 */
const toUpdateMask = (profile) => Object.keys(profile).flatMap((key) => {
    if (key === "contact") {
        return Object.keys(profile.contact ?? {})
            .map((field) => `contact.${field}`)
            .filter(isAdvertiserProfileUpdatablePath);
    }
    return isAdvertiserProfileUpdatablePath(key) ? [key] : [];
});
/**
 * Client-side bound on the research POST. The backend BuildProfile budget is
 * 120s, so aborting just above it turns a hung or silently-dropped request
 * into a recoverable error rather than an unbounded spinner, without cutting
 * off a request the server may still answer in time.
 */
export const ADVERTISER_RESEARCH_TIMEOUT_MS = 130000;
const JSON_HEADERS = { "Content-Type": "application/json" };
/** The service returns the trace id as a response header, not in the body. */
const REQUEST_ID_HEADER = "x-request-id";
/**
 * Advertiser profiles on the seller service.
 *
 * Backed by `${sdk.urls.sellerBaseUrl}/v1/advertiser`. Profiles are typed
 * directly from `@madhive/mad-types/openapi/seller/sellerSchemas` — no local
 * model or transformer, since mad-types already models the wire shape.
 *
 * Three operations on the one resource:
 * - {@link buildProfile} — `POST /v1/advertiser:profile`, which researches an
 *   advertiser and saves the result. Note that the service also mints a
 *   proposal for every call.
 * - {@link find} — `GET /v1/advertiser/{id}` for one, `GET /v1/advertiser`
 *   for a page of them. Observable rather than promise-based specifically so
 *   pagination works: `useLoadListData` only inspects results for a `Page` on
 *   its observable branch, so a promise handler can never populate
 *   `ListData.pagination`. The Proposals and Deals handlers do the same.
 * - {@link getFilterOptions} — `GET /v1/advertiser:filter-options`, the
 *   distinct values the list's filters offer.
 * - {@link save} — `PATCH /v1/advertiser/{id}`.
 */
class AdvertiserProfiles extends ObservableHandler {
    constructor(sdk) {
        super(sdk, "advertiserProfiles", { atomize: true });
    }
    /**
     * Researches an advertiser and saves the resulting profile.
     *
     * The service mints a proposal on every call and returns its id, whether or
     * not the caller wanted one.
     *
     * Aborting through `signal` only stops the caller waiting — the service
     * finishes the run and mints its proposal regardless. It is how a caller
     * stops being blocked, not a way to undo the work.
     *
     * @param request advertiser details, industry, and the requesting user's id
     * @param signal abort signal, to stop waiting on a run
     * @return the built profile, the created proposal's id, and the trace id
     */
    async buildProfile(request, signal) {
        if (!request.user_id) {
            throw new AdvertiserProfileResearchFailed(undefined, "Cannot research an advertiser without a signed-in user.");
        }
        if (!request.advertiser?.name?.trim()) {
            throw new AdvertiserProfileResearchFailed(undefined, "Cannot research an advertiser without a name.");
        }
        try {
            const { data, headers } = await axios.post(`${this.collectionUrl}:profile`, request, { headers: JSON_HEADERS, timeout: ADVERTISER_RESEARCH_TIMEOUT_MS, signal });
            const requestId = headers?.[REQUEST_ID_HEADER];
            return requestId ? { ...data, request_id: requestId } : data;
        }
        catch (error) {
            // A failed run is exactly when the trace id is wanted, so it has to come
            // off the error's response rather than the success path above.
            throw new AdvertiserProfileResearchFailed(error);
        }
    }
    findItems(filters = {}, sort) {
        const idFilter = parseIdFilter(filters);
        const promise = idFilter.size === 1
            ? this.getAdvertiserProfile(String(idFilter.values().next().value))
            : this.getAdvertiserProfiles(filters, sort);
        return new Observable((subscriber) => {
            promise
                .then((result) => {
                subscriber.next(result);
                subscriber.complete();
            })
                .catch((error) => {
                subscriber.error(error);
            });
        });
    }
    async getAdvertiserProfile(id) {
        const url = this.itemUrl(id);
        return this.cache
            .promise(url, () => axios
            .get(url, { headers: JSON_HEADERS })
            .then(({ data }) => (data ? [data] : [])))
            .catch((error) => {
            throw new AdvertiserProfileFetchFailed(error);
        });
    }
    /**
     * The summary fields the list renders — `industry`, `location`, `products`,
     * `proposals_count`, `total_value` — are populated by this endpoint alone;
     * `GET /v1/advertiser/{id}` leaves them unset and returns the full
     * `industries`/`locations`/`products_services` instead.
     *
     * Because the two shapes differ, these rows are not atomized: an atomized
     * row answers a later find by id from cache, and the caller would get the
     * summary where it asked for the researched profile.
     *
     * `paging.token` is replayed verbatim; the endpoint pages forward only.
     */
    async getAdvertiserProfiles(filters, sort) {
        const url = `${this.collectionUrl}${toQueryParams(filters, sort)}`;
        const pageSize = filters?.paging?.size || DEFAULT_PAGE_SIZE;
        try {
            return await this.cache.promise(url, () => axios.get(url, { headers: JSON_HEADERS }).then(({ data }) => {
                const advertisers = data.advertisers ?? [];
                // Only page when the caller asked to. Without `paging` the caller
                // wants a plain list, and wrapping it in a Page would put a page
                // object where an array is expected.
                if (!filters?.paging) {
                    return advertisers;
                }
                return {
                    page: {
                        // total_size is an int64, so it arrives as a string.
                        count: Number(data.total_size ?? 0),
                        token: data.next_page_token,
                        size: pageSize
                    },
                    data: advertisers
                };
            }), { atomize: false });
        }
        catch (error) {
            throw new AdvertiserProfileFetchFailed(error);
        }
    }
    /**
     * Lists the distinct values available for each filter dimension, scoped
     * only by `tab` and `include_archived` — never by any filter the caller has
     * currently applied. Plain rather than routed through `find`, since this
     * isn't a paginated collection of items.
     */
    async getFilterOptions(params = {}) {
        try {
            const { data } = await axios.get(`${this.collectionUrl}:filter-options`, { headers: JSON_HEADERS, params });
            return data;
        }
        catch (error) {
            throw new AdvertiserProfileFetchFailed(error);
        }
    }
    /**
     * Archives an advertiser, or restores one.
     *
     * `archived_at` is OUTPUT_ONLY, so this is the only way to set or clear it —
     * a field-mask update cannot. Archiving an already-archived advertiser
     * returns it unchanged, keeping its original `archived_at`, so a repeated
     * call is not an error. The profile comes back as the service now holds it.
     *
     * @param id the advertiser profile's id
     * @param archived true to archive, false to restore
     * @return the advertiser as it stands after the call
     */
    async setArchived(id, archived) {
        const action = archived ? "archive" : "unarchive";
        if (!id) {
            throw new AdvertiserProfileSaveFailed(undefined, `Cannot ${action} an advertiser without an id.`);
        }
        try {
            const { data } = await axios.post(`${this.itemUrl(id)}:${action}`, { id }, { headers: JSON_HEADERS });
            // `save` and `delete` reconcile inside the handler; this goes through
            // neither, so it reconciles for itself. Without it the cached lists hold
            // the row as it was, and a reload shows an archived advertiser as live.
            this.cache.reconcile("save", data);
            return data;
        }
        catch (error) {
            throw new AdvertiserProfileSaveFailed(error, `Failed to ${action} the advertiser. Please try again at a later time.`);
        }
    }
    saveItem(profile, options = {}) {
        return new Observable((subscriber) => {
            this.patchProfile(profile, options)
                .then((saved) => {
                subscriber.next(saved);
                subscriber.complete();
            })
                .catch((error) => {
                subscriber.error(error);
            });
        });
    }
    async patchProfile(profile, options = {}) {
        if (!profile.id) {
            throw new AdvertiserProfileSaveFailed(undefined, "Cannot save an advertiser profile without an id.");
        }
        const updateMask = options.updateMask ?? toUpdateMask(profile);
        if (!updateMask.length) {
            throw new AdvertiserProfileSaveFailed(undefined, "Cannot save an advertiser profile with no updatable fields.");
        }
        const body = {
            id: profile.id,
            profile,
            update_mask: updateMask.join(","),
            // The service records "unknown" when this is absent, and the history
            // entry cannot be attributed afterwards.
            updated_by: options.updatedBy ?? this.sdk.getCurrentUser()?.email
        };
        try {
            const { data } = await axios.patch(this.itemUrl(profile.id), body, {
                headers: JSON_HEADERS
            });
            return data;
        }
        catch (error) {
            throw new AdvertiserProfileSaveFailed(error);
        }
    }
    deleteItem() {
        throw new NotImplementedError("delete");
    }
    /**
     * @deprecated
     * `make` is not supported — a profile only exists once the service has
     * researched it; see {@link buildProfile}.
     */
    make() {
        throw new NotImplementedError("make");
    }
    get collectionUrl() {
        // Paths match the mad-types-generated fetchers exactly — see
        // `mad-web/packages/core/mad-types/src/openapi/seller/sellerComponents.ts` (`/v1/advertiser`,
        // `/v1/advertiser/{id}`, `/v1/advertiser:profile`).
        return `${this.sdk.urls.sellerBaseUrl}/v1/advertiser`;
    }
    itemUrl(id) {
        return `${this.collectionUrl}/${encodeURIComponent(id)}`;
    }
}
export default AdvertiserProfiles;
//# sourceMappingURL=index.js.map